import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * brenthelsnow: in-stock variants table on the PDP.
 * - "View more" reveals the extra rows of THIS table only (state lives on the host so a morph keeps it).
 * - Clicking a row selects its variant in the picker in place and scrolls to the top (no reload); the first cell
 *   holds a real <a> for keyboard users, no-JS and modifier clicks, and as the fallback when there is no picker.
 * - On variant change the block re-renders itself from the section HTML Horizon already fetched, so the
 *   highlighted row follows the picker without a second request (initial highlight from Liquid, then from
 *   the fetched variant id; a deep-linked ?variant= row past the fold is revealed on connect).
 */
class VariantSkuTable extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#revealSelectedRow();
  }

  /**
   * Marks the row of `variantId` as current (none when null) and expands the table if that row is hidden.
   * @param {string | number | null | undefined} variantId
   */
  #selectRow(variantId) {
    const id = variantId == null ? '' : String(variantId);
    for (const row of this.querySelectorAll('tr[data-variant-id]')) {
      if (row.dataset.variantId === id) {
        row.setAttribute('aria-current', 'true');
      } else {
        row.removeAttribute('aria-current');
      }
    }
    this.#revealSelectedRow();
  }

  #revealSelectedRow() {
    if (this.querySelector('tr[aria-current="true"].variant-sku-table__row--extra')) {
      this.dataset.expanded = 'true';
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.closest('.shopify-section, dialog')?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-more]')) {
      this.dataset.expanded = 'true';
      return;
    }

    // Modifier / non-primary clicks keep native link behaviour (new tab etc.); everything else — including a
    // plain click on the first-cell link — selects in place (Codex r1).
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const row = event.target.closest('tr[data-href]');
    if (!(row instanceof HTMLElement) || !row.dataset.href) return;

    event.preventDefault();
    const href = row.dataset.href;
    this.#scrollToTop();
    this.#selectInPicker(row)
      .then((selected) => {
        if (!selected) window.location.assign(href);
      })
      .catch(() => window.location.assign(href)); // any surprise → the old reload path
  };

  /**
   * Selects the row's variant in the section's variant picker without a page load, one option at a time:
   * set an option's control (radio or <select>), fire `change`, await the picker's fetch + morph, then move to the
   * next option. Sequential because the picker renders only the values available under the current selection
   * (our item 4c hides sold-out colours), so a later option's value may not exist until the earlier one has
   * changed (Codex r2). Resolves false when no picker / a value can't be found — the caller navigates instead.
   * @param {HTMLElement} row
   * @returns {Promise<boolean>}
   */
  async #selectInPicker(row) {
    const section = this.closest('.shopify-section, dialog');
    const picker = section?.querySelector('variant-picker');
    if (!section || !picker) return false;

    /** @type {Record<string, string>} */
    let options;
    try {
      options = JSON.parse(row.dataset.options ?? '');
    } catch {
      return false;
    }

    const targetId = row.dataset.variantId ?? '';
    const token = ++this.#clickToken;

    for (const [name, value] of Object.entries(options)) {
      if (token !== this.#clickToken) return true; // a newer row click took over

      const controls = Array.from(picker.querySelectorAll('input[data-option-name], select'));
      const radios = controls.filter(
        (el) => el instanceof HTMLInputElement && el.dataset.optionName === name
      );
      const select = controls.find(
        (el) =>
          el instanceof HTMLSelectElement && Array.from(el.options).some((opt) => opt.dataset.optionName === name)
      );

      if (radios.length === 0 && !(select instanceof HTMLSelectElement)) continue; // single-value option: no control

      /** @type {HTMLInputElement | HTMLSelectElement | undefined} */
      let trigger;
      if (select instanceof HTMLSelectElement) {
        if (!Array.from(select.options).some((opt) => opt.value === value)) return false;
        if (select.value === value) continue;
        // The picker reads `select option[selected]` (the attribute), not .value — sync both (Codex r1).
        for (const opt of select.options) {
          opt.selected = opt.value === value;
          opt.toggleAttribute('selected', opt.value === value);
        }
        trigger = select;
      } else {
        const radio = radios.find((el) => el instanceof HTMLInputElement && el.value === value);
        if (!(radio instanceof HTMLInputElement)) return false; // hidden under the current selection
        if (radio.checked) continue;
        radio.checked = true;
        trigger = radio;
      }

      this.#pendingVariantId = targetId;
      const settled = new Promise((resolve) => {
        section.addEventListener(
          StandardEvents.productSelect,
          (event) => {
            const promise = /** @type {any} */ (event).promise;
            promise ? promise.then(resolve, resolve) : resolve(undefined);
          },
          { once: true }
        );
      });
      // The picker dispatches shopify:product:select synchronously inside this dispatch; the flag lets
      // #handleProductSelect tell our own event from a shopper's manual change (which cancels the sequence).
      this.#dispatching = true;
      try {
        trigger.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        this.#dispatching = false;
      }
      await settled;
    }

    return true;
  }

  /** Increments per row click — and on any picker change the block didn't make — so a stale sequence stops (Codex r3). */
  #clickToken = 0;

  /** True only while this block is dispatching a `change` into the picker. */
  #dispatching = false;

  /** Horizon scrolls `.page-wrapper` on desktop and the window on mobile (see CUSTOMIZATIONS.md item 19). */
  #scrollToTop() {
    /** @type {Element | null} */
    let node = this.parentElement;
    while (node && node !== document.documentElement) {
      const { overflowY } = getComputedStyle(node);
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        node.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      node = node.parentElement;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /** Variant id a row click asked the picker for; used to correct the URL if the trigger input's data was stale. */
  #pendingVariantId = '';

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    if (!this.#dispatching) {
      // Shopper changed the picker themselves: abandon any in-flight row sequence and its URL correction.
      this.#clickToken++;
      this.#pendingVariantId = '';
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const fresh = html.querySelector('variant-sku-table');
        if (!fresh) return;

        morph(this, fresh, { childrenOnly: true });

        // Picker requests carry option_values=, not variant=, so the morphed Liquid can't know the selection;
        // detail.resource is the picker's selected_or_first_available_variant JSON from the same response (Codex r1).
        this.#selectRow(detail.resource?.id);

        // A row click can change two options at once; the picker writes the trigger input's (possibly stale)
        // data-variant-id into the URL. Put the variant the server actually resolved back into the address bar.
        if (this.#pendingVariantId) {
          this.#pendingVariantId = '';
          const resolvedId = detail.resource?.id;
          if (resolvedId != null && !newProduct) {
            const url = new URL(window.location.href);
            if (url.searchParams.get('variant') !== String(resolvedId)) {
              url.searchParams.set('variant', String(resolvedId));
              history.replaceState({}, '', url.toString());
            }
          }
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[variant-sku-table] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('variant-sku-table')) {
  customElements.define('variant-sku-table', VariantSkuTable);
}
